package com.eladfinish.bible.contest.screens.chapterComplete.features.shuffle.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.gestures.animateScrollBy
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.icons.Icons
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.listSaver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.screens.chapterComplete.features.shuffle.RevealCardData
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.icons.AppIcons
import com.eladfinish.bible.contest.utils.addGershaim
import kotlinx.coroutines.delay
import kotlinx.coroutines.yield
import kotlin.time.Duration.Companion.milliseconds

private const val EXPANSION_LAYOUT_DELAY_MS = 60L
private const val BRING_INTO_VIEW_DELAY_MS = 30L
private const val SCROLL_EXTRA_OFFSET_PX = 24
private val BOTTOM_CHIP_RESERVED_SPACE = 36.dp

@Composable
fun RevealCardDataListUi(
    modifier: Modifier = Modifier,
    backgroundAlpha: Float = 0.4f,
    verses: List<RevealCardData>,
    listState: LazyListState = rememberLazyListState(),
) {
    val expandedMap = rememberSaveable(
        verses,
        saver = listSaver(
            save = { map -> map.filterValues { it }.keys.toList() },
            restore = { keys -> mutableStateMapOf<Int, Boolean>().apply { keys.forEach { put(it, true) } } },
        ),
    ) { mutableStateMapOf<Int, Boolean>() }

    LazyColumn(
        modifier = modifier
            .fillMaxWidth()
            .mouseVerticalScrollbar(listState),
        state = listState,
        contentPadding = navigationBarContentPadding(bottom = 80.dp),
    ) {
        itemsIndexed(
            items = verses,
            key = { index, item ->
                "${index}_${item.otPasuk}_${item.reysha.hashCode()}_${item.seyfa.hashCode()}"
            },
        ) { index, item ->
            val isExpanded = expandedMap[index] ?: false

            ExpandableRevealCard(
                backgroundAlpha = backgroundAlpha,
                index = index + 1,
                revealCardData = item,
                isExpanded = isExpanded,
                onToggle = { expandedMap[index] = !isExpanded },
                listState = listState,
                listIndex = index,
            )
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
internal fun ExpandableRevealCard(
    modifier: Modifier = Modifier,
    backgroundAlpha: Float = 0.4f,
    index: Int,
    revealCardData: RevealCardData,
    isExpanded: Boolean,
    onToggle: () -> Unit,
    listState: LazyListState,
    listIndex: Int,
    onLongClick: (() -> Unit)? = null,
    chipText: String = "פסוק ${revealCardData.otPasuk.addGershaim()}",
) {
    val seyfaBringIntoViewRequester = remember { BringIntoViewRequester() }
    val verseChipBringIntoViewRequester = remember { BringIntoViewRequester() }

    LaunchedEffect(isExpanded) {
        if (!isExpanded) return@LaunchedEffect

        yield()
        delay(EXPANSION_LAYOUT_DELAY_MS.milliseconds)

        val layoutInfo = listState.layoutInfo
        val viewportEnd = layoutInfo.viewportEndOffset
        val viewportStart = layoutInfo.viewportStartOffset

        val itemInfo = layoutInfo.visibleItemsInfo.firstOrNull { it.index == listIndex }

        if (itemInfo == null) {
            listState.animateScrollToItem(
                index = listIndex,
                scrollOffset = -SCROLL_EXTRA_OFFSET_PX,
            )
        } else {
            val itemTop = itemInfo.offset
            val itemBottom = itemInfo.offset + itemInfo.size
            val overflowBottom = itemBottom - viewportEnd
            val overflowTop = viewportStart - itemTop

            when {
                overflowBottom > 0 -> listState.animateScrollBy(
                    value = overflowBottom.toFloat() + SCROLL_EXTRA_OFFSET_PX,
                )
                overflowTop > 0 -> listState.animateScrollBy(
                    value = -(overflowTop.toFloat() + SCROLL_EXTRA_OFFSET_PX),
                )
            }
        }

        delay(BRING_INTO_VIEW_DELAY_MS.milliseconds)
        seyfaBringIntoViewRequester.bringIntoView()
        verseChipBringIntoViewRequester.bringIntoView()
    }

    Card(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 4.dp)
            .combinedClickable(
                onClick = onToggle,
                onLongClick = onLongClick,
            ),
        shape = RoundedCornerShape(8.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color.White.copy(alpha = backgroundAlpha),
        ),
        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.1f)),
        elevation = CardDefaults.cardElevation(),
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .background(Color.Transparent)
                    .padding(bottom = BOTTOM_CHIP_RESERVED_SPACE),
                horizontalAlignment = Alignment.Start,
            ) {
                // --- REYSHA + INDEX ---
                SelectionContainer {
                    Text(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = Color.Black.copy(alpha = 0.6f),
                                    fontWeight = FontWeight.Medium,
                                ),
                            ) {
                                append("($index) ")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = Color.Black,
                                    fontWeight = FontWeight.SemiBold,
                                ),
                            ) {
                                append(revealCardData.reysha)
                            }
                        },
                        modifier = Modifier.padding(vertical = 10.dp, horizontal = 10.dp),
                        textAlign = TextAlign.Start,
                        style = MaterialTheme.typography.bodyLarge.copy(
                            fontSize = 17.sp,
                            textDirection = TextDirection.ContentOrRtl,
                        ),
                    )
                }

                // --- SEYFA (Expanded) ---
                AnimatedVisibility(
                    visible = isExpanded,
                    enter = expandVertically() + fadeIn(),
                    exit = shrinkVertically() + fadeOut(),
                ) {
                    SelectionContainer {
                        SeyfaChip(
                            text = revealCardData.seyfa,
                            modifier = Modifier
                                .padding(horizontal = 10.dp)
                                .padding(bottom = 14.dp)
                                .bringIntoViewRequester(seyfaBringIntoViewRequester),
                        )
                    }
                }

                // --- Collapsed Hint ---
                if (!isExpanded) {
                    Icon(
                        imageVector = AppIcons.Visibility,
                        contentDescription = "הצג תשובה",
                        modifier = Modifier.padding(start = 10.dp, bottom = 14.dp),
                        tint = Color.Black.copy(alpha = 0.55f),
                    )
                }
            }

            // --- Bottom Chip ---
            if (chipText.isNotEmpty()) {
                VerseChip(
                    text = chipText,
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .padding(end = 10.dp, bottom = 8.dp)
                        .bringIntoViewRequester(verseChipBringIntoViewRequester),
                )
            }
        }
    }
}

@Composable
private fun VerseChip(
    text: String,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .background(
                color = Color.Black.copy(alpha = 0.06f),
                shape = RoundedCornerShape(999.dp),
            )
            .padding(horizontal = 10.dp, vertical = 4.dp),
    ) {
        Text(
            text = text,
            color = Color.Black.copy(alpha = 0.75f),
            style = MaterialTheme.typography.labelSmall.copy(
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
                textDirection = TextDirection.ContentOrRtl,
            ),
        )
    }
}

@Composable
private fun SeyfaChip(
    text: AnnotatedString,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .border(
                border = BorderStroke(1.dp, Color.Black.copy(alpha = 0.08f)),
                shape = RoundedCornerShape(12.dp),
            )
            .background(
                color = Color.Black.copy(alpha = 0.035f),
                shape = RoundedCornerShape(12.dp),
            )
            .padding(horizontal = 14.dp, vertical = 12.dp),
    ) {
        Text(
            text = text,
            color = Color.Black.copy(alpha = 0.92f),
            textAlign = TextAlign.Start,
            style = MaterialTheme.typography.bodyMedium.copy(
                fontSize = 16.sp,
                textDirection = TextDirection.ContentOrRtl,
            ),
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun RevealCardDataListUiPreview() {
    val sample = listOf(
        RevealCardData(
            otPasuk = "ב",
            reysha = AnnotatedString("עָזִּ֤י וְזִמְרָת֙ יָ֔הּ וַֽיְהִי־לִ֖י לִֽישׁוּעָ֑ה"),
            seyfa = AnnotatedString("זֶ֤ה אֵלִי֙ וְאַנְוֵ֔הוּ אֱלֹהֵ֥י אָבִ֖י וַאֲרֹֽמְמֶֽנְהוּ׃"),
        ),
        RevealCardData(
            otPasuk = "ג",
            reysha = AnnotatedString("וְֽהָיָ֗ה כְּעֵץ֮ שָׁת֪וּל עַֽל־פַּלְגֵ֫י מָ֥יִם"),
            seyfa = AnnotatedString("אֲשֶׁ֤ר פִּרְי֨וֹ ׀ יִתֵּ֬ן בְּעִתּ֗וֹ וְעָלֵ֥הוּ לֹֽא־יִבּ֑וֹל וְכֹ֖ל אֲשֶׁר־יַֽעֲשֶׂ֣ה יַצְלִֽיחַ׃"),
        ),
    )

    PreviewTheme {
        RevealCardDataListUi(
            verses = sample,
            backgroundAlpha = 0.4f,
        )
    }
}
